import React from 'react';
import {Box, Button, Checkbox, FormControlLabel, Typography} from '@mui/material';
import {CARD_SX, Eyebrow} from '../interventions/v2/primitives.js';
import {COULEUR_NON_AFFECTE, COULEUR_PATRIMOINE, cleCalque, pavEtBacs} from './atelierRules';
import PiluleTrace from './PiluleTrace';

/**
 * Légende de la carte (coin haut droit, carte radius 16) : un interrupteur par calque, groupes Patrimoine · Collecte ·
 * Tournées. Pas de bloc vide : un groupe sans calque n'apparaît pas. Clic sur le libellé d'une journée = la sélectionner.
 * AJOUT À VALIDER (GUIDE_UI) : légende interactive de calques.
 */

function Pastille({couleur, style}) {
    return <span aria-hidden="true" style={{display: 'inline-block', width: 12, height: 12, borderRadius: '50%', flex: 'none',
        background: style?.fillColor ?? couleur, border: `${style?.weight ?? 1}px ${style?.dashArray ? 'dashed' : 'solid'} ${style?.color ?? couleur}`,
        opacity: style?.fillOpacity != null && style.fillOpacity < 0.5 ? 0.8 : 1, boxSizing: 'border-box'}}/>;
}

function Ligne({cle, visible, onToggle, children, onLabelClick, actif}) {
    return (
        <FormControlLabel
            sx={{m: 0, alignItems: 'center', '& .MuiFormControlLabel-label': {fontSize: 12.5, color: '#2D333B', fontWeight: actif ? 800 : 500}}}
            control={<Checkbox size="small" checked={visible(cle)} onChange={() => onToggle(cle)} sx={{p: 0.5}}/>}
            label={<Box component="span" onClick={onLabelClick} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>{children}</Box>}/>
    );
}

/** Lot 9 : « Tout afficher » / « Tout masquer » d'un groupe de calques à plusieurs entrées. */
function ToutBasculer({groupe, cles, onGroupe}) {
    const bouton = (valeur, libelle) => (
        <Button size="small" data-testid={`${groupe}-${valeur ? 'tout-afficher' : 'tout-masquer'}`} onClick={() => onGroupe(cles, valeur)}
                sx={{textTransform: 'none', fontWeight: 700, fontSize: 12, minWidth: 0, py: 0, px: 0.75}}>{libelle}</Button>
    );
    return <Box sx={{display: 'flex', gap: 0.5, mb: 0.25}}>{bouton(true, 'Tout afficher')}{bouton(false, 'Tout masquer')}</Box>;
}

function AtelierLegende({
    nbPav, nbBacs, calquesCollecte, calquesTournees, nbNonAffectes, visible, onToggle, avecTraces, onToggleTraces,
    journeeActive, onSelectJournee, simulationId, onGroupe,
}) {
    return (
        // Cadre absolu borné aux quatre coins de la carte : la légende ne dépasse jamais la carte, le défilement reste interne.
        <Box sx={{position: 'absolute', top: 12, right: 12, bottom: 12, zIndex: 1000, width: 260, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', pointerEvents: 'none'}}>
        <Box sx={{...CARD_SX, flex: '0 1 auto', minHeight: 0, maxHeight: '100%', overflowY: 'auto', p: 1.5, pointerEvents: 'auto'}}
             data-testid="legende">
            <Eyebrow sx={{mb: 0.5}}>Calques</Eyebrow>
            <Ligne cle={cleCalque.patrimoine} visible={visible} onToggle={onToggle}>
                <Pastille couleur={COULEUR_PATRIMOINE}/>Patrimoine · {pavEtBacs(nbPav, nbBacs)}
            </Ligne>

            {calquesCollecte.length > 0 &&
                <Box sx={{mt: 1}} data-testid="legende-collecte">
                    <Eyebrow sx={{mb: 0.25}}>Collecte</Eyebrow>
                    {calquesCollecte.length > 1 && onGroupe && <ToutBasculer groupe="collecte" cles={calquesCollecte.map((c) => cleCalque.collecte(c.cle))} onGroupe={onGroupe}/>}
                    {calquesCollecte.map((c) => (
                        <Ligne key={c.cle} cle={cleCalque.collecte(c.cle)} visible={visible} onToggle={onToggle}>
                            <span aria-hidden="true" style={{color: c.couleur, fontSize: 15, lineHeight: 1, width: 14, textAlign: 'center', flex: 'none'}}>{c.picto}</span>{c.libelle}
                        </Ligne>
                    ))}
                </Box>}

            {calquesTournees.length > 0 &&
                <Box sx={{mt: 1}} data-testid="legende-tournees">
                    <Eyebrow sx={{mb: 0.25}}>Tournées</Eyebrow>
                    {calquesTournees.length > 1 && onGroupe && <ToutBasculer groupe="tournees" cles={calquesTournees.map((c) => cleCalque.tournee(c.cle))} onGroupe={onGroupe}/>}
                    {calquesTournees.map((c) => (
                        <Ligne key={c.cle} cle={cleCalque.tournee(c.cle)} visible={visible} onToggle={onToggle}
                               actif={journeeActive === c.cle} onLabelClick={() => onSelectJournee(c.cle)}>
                            <Pastille couleur={c.couleur}/>{c.libelle}
                            {avecTraces && simulationId && visible(cleCalque.tournee(c.cle)) && <PiluleTrace simulationId={simulationId} date={c.date} equipe={c.equipe}/>}
                        </Ligne>
                    ))}
                    {nbNonAffectes > 0 &&
                        <Typography component="div" sx={{display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, mt: 0.5, ml: 0.5}}>
                            <Pastille couleur={COULEUR_NON_AFFECTE}/>Non affectées · {nbNonAffectes}
                        </Typography>}
                    <FormControlLabel sx={{m: 0, mt: 0.5, '& .MuiFormControlLabel-label': {fontSize: 12.5}}}
                                      control={<Checkbox size="small" checked={avecTraces} onChange={onToggleTraces} sx={{p: 0.5}}/>}
                                      label="Tracés des journées"/>
                </Box>}
        </Box>
        </Box>
    );
}

export default AtelierLegende;
